import { ChoiceClass, ChoiceData, ChoiceEnum } from "../vote/choice.class";
import { ChoiceList } from "./choice-list.class";
import cloneDeep from 'lodash/cloneDeep';

export interface VoteData {
    choicesArray?: (ChoiceEnum | ChoiceData)[];
    updatedAt?: any;
    createdAt?: any;
    receiveUpdates?: boolean;
    userName?: string;
    userEmail?: string;
    userPhone?: string;
    userId?: string;
    choiceInvitation?: number;
    adultsCount?: number;
    kidsCount?: number;
    adultVegetariansCount?: number;
    kidVegetariansCount?: number;
    privateComment?: string,
    publicComment?: string,
    customFlag1?: string,
    customFlag2?: string,
    onBehalf?: boolean

}

/**
 * The contact part of a vote. Persisted separately under whoo/votePrivate/{ownerId}/{pollId}/{voterId},
 * readable only for the poll owner (plus delegates) and the voter himself — while the rest of the
 * vote stays in the publicly readable whoo/votes node.
 */
export interface VotePrivateData {
    userEmail?: string;
    userPhone?: string;
    privateComment?: string;
}

/** The VoteData fields that are persisted in the private node instead of the public one. */
export const votePrivateFields: (keyof VotePrivateData)[] = ['userEmail', 'userPhone', 'privateComment'];
/**
 * A vote is one users response to a poll
 * It consists of multiple choices that are answers to the "poll-options" (questions)
 *Here ist the relation between Poll and Vote
 *
 *      Poll-Owner                  Participant
 *
 *      Poll-Header (Poll)          Vote (Response)
 *      Poll-Option (Question)      Choice (Answer)
 *      Multiple-Choice-List        Secondary-Answer-List
 */

export class VoteClass {
    private _voteData: VoteData = {};
    private _choiceList: ChoiceList

    constructor(voteData: VoteData, numperOfOptions: number) {
        if (voteData) {
            this._voteData = voteData // @todo clone and remove choicesArray from this._votesData
        }
        if (!voteData.choicesArray) voteData.choicesArray = [];
        this._choiceList = new ChoiceList().reInit(data => new ChoiceClass(data), voteData.choicesArray, numperOfOptions);
    }

    /**
     * Replaces the whole vote content in place: every field of voteData (cloned) becomes the
     * new raw data, the choice list is rebuilt from voteData.choicesArray and padded to
     * numberOfOptions. In place, so references to this instance (form bindings, the row in the
     * vote matrix) stay valid. Contact fields are replaced as well - a caller who wants to keep
     * them takes dataPrivate before and applyPrivateData after.
     * The constructor does the same on a fresh instance, except that it keeps (and completes)
     * the passed object instead of cloning it.
     */
    public replaceData(voteData: VoteData, numberOfOptions: number): void {
        this._voteData = cloneDeep(voteData ?? {});
        if (!this._voteData.choicesArray) this._voteData.choicesArray = [];
        this._choiceList = new ChoiceList().reInit(data => new ChoiceClass(data), this._voteData.choicesArray, numberOfOptions);
    }

    public initChoiceList(length: number) {
        this._choiceList.init(length, new ChoiceClass())
    }

    get choiceList(): ChoiceList {
        return this._choiceList
    }

    /**
     * The persisted form of a vote.
     * choicesArray is taken from the choice list as a dense array. Assigning it (instead of
     * merging into the cloned raw data) makes sure a vote that was loaded in firebase map-form
     * is written back as a real array node - and that no stale entries survive.
     */
    get data(): VoteData {
        const data: VoteData = cloneDeep(this._voteData)
        data.choicesArray = this._choiceList.data
        return data;
    }

    /**
     * The persisted form of the public part of the vote: everything except the contact
     * fields (see votePrivateFields), which go to the access-restricted votePrivate node.
     */
    get dataPublic(): VoteData {
        const data: VoteData = this.data;
        votePrivateFields.forEach(field => { delete data[field] });
        return data;
    }

    /**
     * The persisted form of the contact part of the vote. undefined fields are left out
     * (firebase rejects undefined values); returns null when no contact field is set,
     * so callers can skip writing the node altogether.
     */
    get dataPrivate(): VotePrivateData | null {
        const data: VotePrivateData = {};
        votePrivateFields.forEach(field => {
            if (this._voteData[field] !== undefined) {
                data[field] = this._voteData[field];
            }
        });
        return Object.keys(data).length > 0 ? data : null;
    }

    /**
     * Merges a separately loaded private part (votePrivate node) into the vote.
     * A missing node (null/undefined) is fine — votes from before the backfill
     * simply have no private part yet.
     */
    public applyPrivateData(privateData: VotePrivateData | null | undefined): void {
        if (!privateData) return;
        votePrivateFields.forEach(field => {
            if (privateData[field] !== undefined) {
                this._voteData[field] = privateData[field];
            }
        });
    }

    public getChoiceEnum(index: number): ChoiceEnum {
        if (index < this.choiceArrayLength) {
            return this.choiceList[index]?.choiceEnum
        } else {
            return ChoiceEnum.notSelected
        }
    }

    public get choiceArrayLength(): number {
        // the choice list is the normalized source of truth - the raw choicesArray may still
        // be in firebase map-form and would report an undefined length
        return this._choiceList.length
    }

    get updatedAt(): any | undefined {
        return this._voteData.updatedAt;
    }
    set updatedAt(value: any | undefined) {
        this._voteData.updatedAt = value;
    }

    get createdAt(): any | undefined {
        return this._voteData.createdAt;
    }
    set createdAt(value: any | undefined) {
        this._voteData.createdAt = value;
    }

    get receiveUpdates(): boolean | undefined {
        return this._voteData.receiveUpdates;
    }
    set receiveUpdates(value: boolean | undefined) {
        this._voteData.receiveUpdates = value;
    }

    get userName(): string | undefined {
        return this._voteData.userName;
    }
    set userName(value: string | undefined) {
        this._voteData.userName = value;
    }

    get userEmail(): string | undefined {
        return this._voteData.userEmail;
    }
    set userEmail(value: string | undefined) {
        this._voteData.userEmail = value;
    }

    get userPhone(): string | undefined {
        return this._voteData.userPhone;
    }
    set userPhone(value: string | undefined) {
        this._voteData.userPhone = value;
    }

    get userId(): string | undefined {
        return this._voteData.userId;
    }
    set userId(value: string | undefined) {
        this._voteData.userId = value;
    }

    get choiceInvitation(): number | undefined {
        return this._voteData.choiceInvitation;
    }
    /**
     * undefined removes the field instead of storing an undefined value: data/dataPublic
     * clone the raw data (cloneDeep keeps undefined-valued keys) and firebase rejects a
     * multi path update that contains undefined ("values argument contains undefined").
     */
    set choiceInvitation(value: number | undefined) {
        if (value === undefined) {
            delete this._voteData.choiceInvitation;
        } else {
            this._voteData.choiceInvitation = value;
        }
    }

    /**
     * Persons this vote registers against the header limit (header.maxPersons): an accepted
     * vote counts at least 1 (no counter -> adultsCount 1, legacy votes may lack the field)
     * plus the kids; declined, unanswered and malformed counts contribute 0. Mirrored in
     * functions/src/whocan/vote-counts.ts for the public aggregate - keep both in sync.
     */
    get registeredPersons(): number {
        if (this._voteData.choiceInvitation !== 1) return 0;
        const adults = Number(this._voteData.adultsCount);
        const kids = Number(this._voteData.kidsCount);
        return Math.max(1, adults > 0 ? adults : 0) + (kids > 0 ? kids : 0);
    }

    get peopleCount(): number {
        return this.adultsCount + this.kidsCount;
    }

    get vegetariansCount(): number {
        return this.adultVegetariansCount + this.kidVegetariansCount;
    }

    get adultsCount(): number {
        if (this._voteData.adultsCount === undefined || this._voteData.choiceInvitation === 0) {
            return 0
        } else {
            return this._voteData.adultsCount;
        }
    }
    set adultsCount(value: number | undefined) {
        this._voteData.adultsCount = value;
    }

    get kidsCount(): number {
        if (this._voteData.kidsCount === undefined || this._voteData.choiceInvitation === 0) {
            return 0
        } else {
            return this._voteData.kidsCount;
        }
    }
    set kidsCount(value: number | undefined) {
        this._voteData.kidsCount = value;
    }

    get adultVegetariansCount(): number {
        if (this._voteData.adultVegetariansCount === undefined || this._voteData.choiceInvitation === 0) {
            return 0
        } else {
            return this._voteData.adultVegetariansCount;
        }
    }
    set adultVegetariansCount(value: number | undefined) {
        this._voteData.adultVegetariansCount = value;
    }

    get kidVegetariansCount(): number {
        if (this._voteData.kidVegetariansCount === undefined || this._voteData.choiceInvitation === 0) {
            return 0
        } else {
            return this._voteData.kidVegetariansCount;
        }
    }

    set kidVegetariansCount(value: number | undefined) {
        this._voteData.kidVegetariansCount = value;
    }

    get privateComment(): string | undefined {
        return this._voteData.privateComment;
    }
    set privateComment(value: string | undefined) {
        this._voteData.privateComment = value;
    }

    get publicComment(): string | undefined {
        return this._voteData.publicComment;
    }
    set publicComment(value: string | undefined) {
        this._voteData.publicComment = value;
    }

    get customFlag1(): string | undefined {
        return this._voteData.customFlag1;
    }
    set customFlag1(value: string | undefined) {
        this._voteData.customFlag1 = value;
    }

    get customFlag2(): string | undefined {
        return this._voteData.customFlag2;
    }
    set customFlag2(value: string | undefined) {
        this._voteData.customFlag2 = value;
    }

    public cloneDeep(): VoteClass {
        return cloneDeep(this);
    }
}
